import {
  getShopProductArtifactSchema,
  getShopProductByEanArgsSchema,
  getShopProductByIsbnArgsSchema,
  getShopProductByExternalIdArgsSchema,
  getShopProductByProductIdArgsSchema,
  getShopProductContentSchema,
  type GetShopProductArtifact,
} from '@kanbu/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { ChevronLeft, ChevronRight, Gift, Package, Tag } from 'lucide-react';
import { motion } from 'motion/react';
import { useContext, useState } from 'react';
import { z } from 'zod';

import { ChatRendererContext } from '../../components/chat-renderer/chat-renderer-provider';
import { formatPrice } from '../../lib/format-price';
import { Card, Button } from '../../ui';
import { ToolError, ToolStatus } from '../components';
import { createToolComponent } from '../tool-registry';

type ProductAttributeRow = GetShopProductArtifact['attributes'][number];

export interface GroupedProductAttribute {
  attributeId: string;
  label: string;
  values: string[];
}

/**
 * A multi-value attribute is stored as one DB row per value under the same
 * attribute definition. Collapse them to a single "Barva: a, b, c" row for
 * the detail card.
 */
export function groupProductAttributes(
  attributes: ProductAttributeRow[],
): GroupedProductAttribute[] {
  const groups = new Map<string, GroupedProductAttribute>();

  for (const row of attributes) {
    const attributeId = row.attribute.id;
    const existing = groups.get(attributeId);

    if (existing) {
      existing.values.push(row.value);
      continue;
    }

    groups.set(attributeId, {
      attributeId,
      label: row.attribute.displayName || row.attribute.name,
      values: [row.value],
    });
  }

  return [...groups.values()];
}

/**
 * Bigger sibling of the carousel's image gallery (same hover-to-reveal
 * prev/next controls), used on the single detail card where the image gets
 * a full-width column instead of ~80% of a scroll snap point.
 */
function ProductGallery({ images, alt }: { images: string[]; alt: string }) {
  const { t } = useLingui();
  const [imageIndex, setImageIndex] = useState(0);
  const activeImage = imageIndex % (images.length || 1);

  if (images.length === 0) {
    return null;
  }

  return (
    <div className='group/gallery relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-chat-background-secondary'>
      <img
        src={images[activeImage]}
        alt={alt}
        loading='lazy'
        draggable={false}
        className='absolute inset-0 size-full object-cover'
      />
      {images.length > 1 ? (
        <div className='absolute inset-0 opacity-0 transition-opacity group-hover/gallery:opacity-100 [&:has(:focus-visible)]:opacity-100 [@media(hover:none)]:opacity-100'>
          <button
            type='button'
            aria-label={t`Previous image`}
            onClick={() =>
              setImageIndex((activeImage + images.length - 1) % images.length)
            }
            className='absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-chat-background/90 p-1.5 text-chat-foreground'
          >
            <ChevronLeft className='size-5' />
          </button>
          <button
            type='button'
            aria-label={t`Next image`}
            onClick={() => setImageIndex((activeImage + 1) % images.length)}
            className='absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-chat-background/90 p-1.5 text-chat-foreground'
          >
            <ChevronRight className='size-5' />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Collapsible({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  if (isOpen) {
    return children;
  }

  return (
    <div className='space-y-2 max-h-32px relative'>
      <div className='absolute inset-0 bg-gradient-to-b from-transparent to-black/50' />
      <div className='overflow-hidden h-32'>{children}</div>
      <div className='absolute left-1/2 -translate-x-1/2 bottom-2'>
        <Button
          variant='secondary'
          size='xs'
          onClick={() => setIsOpen(!isOpen)}
        >
          <Trans>Show more</Trans>
        </Button>
      </div>
    </div>
  );
}

export const GetShopProductTool = createToolComponent(
  {
    validate: false,
    schema: {
      args: z.union([
        getShopProductByProductIdArgsSchema,
        getShopProductByExternalIdArgsSchema,
        getShopProductByEanArgsSchema,
        getShopProductByIsbnArgsSchema,
      ]),
      artifact: getShopProductArtifactSchema,
      content: getShopProductContentSchema,
    },
  },
  ({ state, artifact, error, toolCall }) => {
    const chat = useContext(ChatRendererContext);
    const { t } = useLingui();

    switch (state) {
      case 'call':
        return (
          <ToolStatus loading>
            <Trans>Fetching product details...</Trans>
          </ToolStatus>
        );

      case 'call-partial':
      case 'call-complete':
        return (
          <ToolStatus loading>
            <Trans>Parsing product details...</Trans>
          </ToolStatus>
        );

      case 'error':
        return (
          <ToolError message={<Trans>Product not found</Trans>} error={error} />
        );

      case 'result': {
        const images = [
          ...new Set(
            [artifact?.imageUrl, ...(artifact?.images ?? [])].flatMap(url =>
              url ? [url] : [],
            ),
          ),
        ];
        const description = artifact?.shortSummary || artifact?.description;
        const hasExtraDetails = Boolean(
          (artifact?.attributes && artifact.attributes.length > 0) ||
            (artifact?.gifts && artifact.gifts.length > 0) ||
            (artifact?.accessories && artifact.accessories.length > 0),
        );

        return (
          <motion.div
            className='w-full max-w-full overflow-hidden'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card.Root className='overflow-hidden border border-chat-border shadow-none'>
              <ProductGallery
                images={images}
                alt={artifact?.title || artifact?.name || ''}
              />

              <Card.Content className='space-y-3 p-4'>
                <Card.Title className='text-lg leading-snug'>
                  {artifact?.title || artifact?.name}
                </Card.Title>

                {description ? (
                  <p className='line-clamp-6 text-sm text-muted-foreground'>
                    {description}
                  </p>
                ) : null}

                <div className='flex items-center justify-between gap-2'>
                  <span className='shrink-0 whitespace-nowrap text-base font-bold text-chat-foreground'>
                    {formatPrice(artifact?.price, artifact?.currency)}
                  </span>
                  <div className='flex shrink-0 items-center gap-1'>
                    <Button
                      type='button'
                      size='xs'
                      variant='ghost'
                      className='h-6 px-2 text-muted-foreground hover:bg-chat-accent hover:text-chat-foreground'
                      disabled={!chat?.append || chat.isFetching}
                      onClick={() =>
                        artifact &&
                        chat?.append?.(
                          t`Find products similar to ${artifact.name} (${artifact.url}).`,
                          { uiAction: 'find_similar_products' },
                        )
                      }
                    >
                      <Trans>Similar</Trans>
                    </Button>
                    <Button
                      asChild
                      size='xs'
                      variant='secondary'
                      className='h-6 shrink-0'
                    >
                      <a
                        href={artifact?.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <Trans>Details</Trans>
                      </a>
                    </Button>
                  </div>
                </div>

                {hasExtraDetails && artifact ? (
                  <Collapsible>
                    <div className='space-y-2'>
                      {/* Attributes section */}
                      {artifact.attributes &&
                        artifact.attributes.length > 0 && (
                          <div className='space-y-2'>
                            <div className='flex items-center gap-2 text-chat-foreground font-semibold'>
                              <Tag size={16} />
                              <h3>
                                <Trans>Specifications</Trans>
                              </h3>
                            </div>
                            <div className='flex flex-col gap-y-2 text-sm'>
                              {groupProductAttributes(artifact.attributes).map(
                                group => (
                                  <div
                                    key={group.attributeId}
                                    className='flex justify-between gap-1'
                                  >
                                    <span className='text-muted-foreground font-semibold'>
                                      {group.label}:
                                    </span>
                                    <span className='font-medium text-right'>
                                      {group.values.join(', ')}
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {/* Gifts section */}
                      {artifact.gifts && artifact.gifts.length > 0 && (
                        <div className='space-y-2'>
                          <div className='flex items-center gap-2 text-chat-foreground font-semibold'>
                            <Gift size={16} />
                            <h3>
                              <Trans>Included gifts</Trans>
                            </h3>
                          </div>
                          <div className='space-y-1 ml-2'>
                            {artifact.gifts.map(gift => (
                              <div
                                key={gift}
                                className='flex items-center gap-2'
                              >
                                <span className='px-2 py-1 bg-chat-background-secondary'>
                                  {gift}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Accessories section */}
                      {artifact.accessories &&
                        artifact.accessories.length > 0 && (
                          <div className='space-y-2'>
                            <div className='flex items-center gap-2 text-chat-foreground font-semibold'>
                              <Package size={16} />
                              <h3>
                                <Trans>Recommended accessories</Trans>
                              </h3>
                            </div>
                            <ul className='list list-disc text-sm ml-4'>
                              {artifact.accessories.map(accessory => (
                                <li key={accessory.id}>
                                  <a
                                    href={accessory.url}
                                    className='hover:underline cursor-pointer'
                                  >
                                    {accessory.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  </Collapsible>
                ) : null}
              </Card.Content>
            </Card.Root>
          </motion.div>
        );
      }

      default:
        return null;
    }
  },
);
